import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import StatCard from '../../../../shared/layout/cards/StatCard';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import { MdTrendingUp, MdStorefront } from 'react-icons/md';
import { fetchDistributorRetailerDetail } from '../../../../services/Http/DistributorApi';
import { useCurrency } from '../../../../context/CurrencyProvider';

const metricConfig = [
    { key: 'outbound_value', label: 'DistributorRetailerDetail.metrics.outboundValue', icon: MdTrendingUp, format: 'currency', color: 'totalVolume' },
    { key: 'outbound_quantity', label: 'DistributorRetailerDetail.metrics.outboundQuantity', icon: MdTrendingUp, format: 'number', color: 'transactions' },
    { key: 'manufacturers_count', label: 'DistributorRetailerDetail.metrics.manufacturers', icon: MdStorefront, format: 'number', color: 'retailers' },
];

const DistributorRetailerDetail = () => {
    const { retailerId } = useParams();
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const formatAmount = useCallback((value, localAmount = null, localCurrencyId = null, localCurrencyCode = null) => {
        return formatResolvedAmount({
            eurAmount: value,
            localAmount,
            localCurrencyId,
            localCurrencyCode,
        });
    }, [formatResolvedAmount]);

    useEffect(() => {
        const loadDetail = async () => {
            try {
                const response = await fetchDistributorRetailerDetail(retailerId);
                setData(response);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadDetail();
    }, [retailerId]);

    if (loading) {
        return <LoadingMessage>{t('DistributorRetailerDetail.loading')}</LoadingMessage>;
    }

    if (error || !data) {
        return <ErrorMessage>{t('DistributorRetailerDetail.error')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <div>
                    <PageTitle>{data.retailer.name}</PageTitle>
                    <Subtitle>{data.retailer.description || t('DistributorRetailerDetail.subtitle')}</Subtitle>
                </div>
                <BackLink to="/retailers">{t('DistributorRetailerDetail.back')}</BackLink>
            </PageHeader>
            <PageContent>
                <StatsRow>
                    {metricConfig.map(({ key, label, icon: Icon, format, color }) => (
                        <StatCard
                            key={key}
                            icon={<Icon className="icon" />}
                            title={t(label)}
                            value={formatValue({
                                value: data.metrics[key],
                                localAmount: key === 'outbound_value' ? data.metrics.outbound_value_local : null,
                                localCurrencyId: key === 'outbound_value' ? data.metrics.seller_currency_id : null,
                            }, format, formatAmount)}
                            color={color}
                        />
                    ))}
                </StatsRow>

                <Panels>
                    <Panel>
                        <PanelTitle>{t('DistributorRetailerDetail.manufacturers')}</PanelTitle>
                        {renderManufacturers(data.manufacturers, t)}
                    </Panel>
                    <Panel>
                        <PanelTitle>{t('DistributorRetailerDetail.recentOrders')}</PanelTitle>
                        {renderOrders(data.recent_orders, t, formatAmount)}
                    </Panel>
                </Panels>
            </PageContent>
        </PageContainer>
    );
};

const renderManufacturers = (items, t) => {
    if (!items || items.length === 0) {
        return <EmptyState>{t('DistributorRetailerDetail.empty')}</EmptyState>;
    }
    return (
        <List>
            {items.map((item) => (
                <li key={`${item.id}-${item.category}`}>
                    <span>{item.name}</span>
                    <small>{item.category}</small>
                </li>
            ))}
        </List>
    );
};

const renderOrders = (items, t, formatAmount) => {
    if (!items || items.length === 0) {
        return <EmptyState>{t('DistributorRetailerDetail.empty')}</EmptyState>;
    }
    return (
        <List>
            {items.map((item, idx) => (
                <li key={`order-${idx}`}>
                    <div>
                        <strong>{item.manufacturer}</strong>
                        <small>{item.date}</small>
                    </div>
                    <span>{formatValue({
                        value: item.value,
                        localAmount: item.value_local,
                        localCurrencyId: item.seller_currency_id,
                    }, 'currency', formatAmount)}</span>
                </li>
            ))}
        </List>
    );
};

const formatValue = (value, formatType = 'number', formatAmount = (v) => v) => {
    const metric = typeof value === 'object' && value !== null ? value : { value };
    const rawValue = metric.value;

    if (rawValue === null || rawValue === undefined) {
        return '0';
    }
    const numeric = Number(rawValue);
    if (Number.isNaN(numeric)) {
        return rawValue;
    }
    if (formatType === 'currency') {
        return formatAmount(
            numeric,
            metric.localAmount,
            metric.localCurrencyId,
            metric.localCurrencyCode
        );
    }
    return numeric.toLocaleString();
};

const Subtitle = styled.p`
    margin: 0.25rem 0 0;
    color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
`;

const BackLink = styled(Link)`
    color: #821692;
    font-weight: 600;
`;

const StatsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const Panels = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.25rem;
`;

const Panel = styled.div`
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    padding: 1.25rem;
`;

const PanelTitle = styled.h4`
    margin-top: 0;
`;

const List = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;

    li {
        display: flex;
        justify-content: space-between;
        padding: 0.625rem 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    li:last-child {
        border-bottom: none;
    }

    small {
        color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
    }
`;

const EmptyState = styled.p`
    color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
`;

export default DistributorRetailerDetail;
